
import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/utils/styles';
import { Sidebar, SidebarRail, useSidebar } from '@/components/ui/sidebar';

import { MainSidebarContent } from '../content';
import { MainSidebarFooter } from '../footer';
import { MainSidebarHeader } from '../header/sidebar-header';
import { SupportFeedbackModal, type SupportFeedbackType } from '@/features/feedback-support';
import { useSupportFeedback } from '@/features/feedback-support/hooks';
import { useNotifications } from '@/features/notifications';

import { getNavUserItems } from '../../config/nav-user-items.config';
import { getSidebarContent } from '../../config/sidebar-content.config';
import { useSidebarState } from '../../hooks/use-sidebar-state';

export function MainSidebar({ className, ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation(['sidebar']);
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<SupportFeedbackType>('support');

  const { add } = useNotifications();
  const sidebarState = useSidebarState();

  const { submitFeedback } = useSupportFeedback({
    onSuccess: () => {
      add({
        id: new Date().toString(),
        title: t('Success'),
        description: t('Your message has been sent successfully'),
      });
    },
    onError: (error) => {
      add({
        id: new Date().toString(),
        title: t('Error'),
        description: t('Failed to send message. Please try again.'),
      });
      console.error('Support/Feedback submission error:', error);
    },
  });

  const navUserItems = useMemo(() => getNavUserItems(t, navigate), [t, navigate]);

  const contentItems = useMemo(
    () =>
      getSidebarContent({
        t,
        ...sidebarState,
      }),
    [t, sidebarState]
  );

  const handleSupportClick = () => {
    setModalType('support');
    setModalOpen(true);
  };

  const handleFeedbackClick = () => {
    setModalType('feedback');
    setModalOpen(true);
  };

  const handleSubmit = async (message: string, type: SupportFeedbackType) => {
    await submitFeedback(message, type);
  };

  return (
    <>
      <Sidebar variant="inset" collapsible="icon" className={cn('bg-primary-900', className)} {...props}>
        <MainSidebarHeader isCollapsed={isCollapsed} />

        <MainSidebarContent config={contentItems} isCollapsed={isCollapsed} />

        <MainSidebarFooter
          className="p-2"
          userMenuOptions={navUserItems}
          onSupportClick={handleSupportClick}
          onFeedbackClick={handleFeedbackClick}
        />
        <SidebarRail />
      </Sidebar>

      <SupportFeedbackModal open={modalOpen} onOpenChange={setModalOpen} type={modalType} onSubmit={handleSubmit} />
    </>
  );
}
